import { Close as CloseIcon } from '@mui/icons-material';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    Typography,
    useTheme,
} from '@mui/material';
import { Field, Form, FormikProvider, useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import { useEffect, useMemo, useState } from 'react';
import * as Yup from 'yup';

import { Payer, PaymentTypes, RoleScope } from '../../TaxPayment/constants';
import ReceiptUploader from '../../TaxPayment/ReceiptUploader/ReceiptUploader';

import states from 'modules/common/constants/states';
import AmountField from 'modules/common/components/MUI/AmountField';
import DateField from 'modules/common/components/MUI/DateField';
import Select from 'modules/common/components/MUI/Select';
import useTrigger from 'modules/common/useTrigger';
import useCreateTaxPaymentMutation from 'modules/Tax/queries/useCreateTaxPaymentMutation';
import type { PaymentType } from 'modules/Tax/queries/invalidateCalculatorsForPayment';
import useRefreshPaymentCaches from 'modules/Tax/queries/useRefreshPaymentCaches';
import { QTEType } from 'modules/Tax/types';
import { convertDateToBackendFormat, taxQuarterOptions } from 'modules/Tax/utils/dates';
import generateYearsOptions from 'modules/common/generateYearsOptions/generateYearsOptions';

const validationSchema = Yup.object().shape({
    tax_type: Yup.string().required('Required'),
    payee: Yup.string().required('Required'),
    amount: Yup.number().label('Amount').typeError('Required').required('Required'),
    payer: Yup.string().required('Required'),
    payment_type: Yup.string().required('Required'),
    paid_at: Yup.string()
        .label('Date')
        .test('future-date', 'Payment date cannot be in the future', (value) => {
            if (!value) {
                return true;
            }
            const date = new Date(value);
            return date <= new Date();
        })
        .nullable()
        .required('Required'),
    year: Yup.number().typeError('Required').min(1900).required('Required'),
    quarter: Yup.number().typeError('Required').oneOf([1, 2, 3, 4], 'Quarter must be 1-4').required('Required'),
});

interface RecordPaymentModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    prefilledValues: {
        tax_type?: QTEType;
        amount?: number;
        payee?: string;
        paid_at?: string;
        quarter?: number;
        year?: number;
    };
}

const taxTypeOptions = [
    { value: QTEType.FEDERAL, label: 'Federal' },
    { value: QTEType.STATE, label: 'State' },
    { value: QTEType.BUSINESS, label: 'Business' },
];

const stateOptions = Object.entries(states).map(([code, name]) => ({
    value: code,
    label: String(name),
}));

/** Tax types whose payee is a state, so the federal 'US' payee is never a valid default for them. */
function isJurisdictionalTaxType(taxType?: string): boolean {
    return taxType === QTEType.STATE || taxType === QTEType.BUSINESS;
}

function RecordPaymentModal({ isOpen, onClose, onSuccess, prefilledValues }: RecordPaymentModalProps) {
    const theme = useTheme();
    const [trigger, triggerUpdate] = useTrigger();
    const createPaymentMutation = useCreateTaxPaymentMutation();
    const refreshPaymentCaches = useRefreshPaymentCaches();
    const { enqueueSnackbar } = useSnackbar();
    const [isUploadingReceipt, setIsUploadingReceipt] = useState(false);

    const yearOptionsData = useMemo(() => generateYearsOptions(7, 0, 0), []); // dynamically updates year options based off of current year
    const yearOptions = yearOptionsData.options;

    const initialValues = useMemo(
        () => ({
            tax_type: prefilledValues?.tax_type || 'federal',
            amount: prefilledValues?.amount || null,
            /*
             * 'US' is the federal payee, and it is not a key in `states` — so falling back to it for
             * a state or business payment opened the State select on a value not in its own options.
             * Yup only checks non-empty, so it saved: a franchise payment recorded against payee
             * 'US'. Empty instead, which is what `required` is there to catch. Callers that know the
             * jurisdiction still prefill it.
             */
            payee: prefilledValues?.payee || (isJurisdictionalTaxType(prefilledValues?.tax_type) ? '' : 'US'),
            payer: Payer.INDIVIDUAL,
            /*
             * Derived from the prefilled tax type, not hardcoded to quarterly.
             *
             * The effect below corrects `payment_type` when `tax_type` changes, which covers the
             * member picking Business from the dropdown. It does not cover a modal opened already
             * set to Business: `tax_type` never changes, so the effect never re-fires — and with
             * `enableReinitialize`, a new `prefilledValues` identity resets the form back to these
             * values afterwards. The dropdown read "Business" while the payload still said
             * `quarterly`, so the payment was recorded as a state one and then vanished from the
             * Fees and Franchise history, which filters on `business`.
             */
            payment_type:
                prefilledValues?.tax_type === QTEType.BUSINESS ? PaymentTypes.BUSINESS : PaymentTypes.QUARTERLY,
            paid_at: prefilledValues?.paid_at || null,
            quarter: prefilledValues?.quarter || null,
            // Convert year to string since yearOptions uses string values
            year: prefilledValues?.year ? String(prefilledValues.year) : null,
            replace_file: false,
        }),
        [prefilledValues]
    );

    const handleSuccess = () => {
        enqueueSnackbar('Payment recorded successfully', { variant: 'success' });
        onSuccess();
    };

    const handleError = () => {
        setIsUploadingReceipt(false);
        enqueueSnackbar('Failed to record payment', { variant: 'error' });
    };

    const onSubmit = async (values: typeof initialValues) => {
        /*
         * `replace_file` only means the member touched the receipt field — nothing clears it when
         * the file is removed. Uppy no-ops on an empty file list, so the flag alone made Record do
         * nothing at all: no request, no error, no snackbar. The uploader mirrors the staged file
         * onto `file`, so that is what says whether there is a receipt to send.
         */
        const stagedReceipt = (values as typeof values & { file?: File | null }).file;
        if (values.replace_file && stagedReceipt) {
            /*
             * Uppy posts on its own and reports back only through onUploadSuccess/onUploadError, so
             * there is no mutation to read a pending state off — track the hand-off here, or this
             * path leaves Record clickable for the whole upload and posts a payment per click.
             */
            setIsUploadingReceipt(true);
            return triggerUpdate();
        }

        const payload = {
            amount: Number(values.amount),
            payee: values.payee,
            payer: values.payer,
            payment_type: values.payment_type,
            paid_at: convertDateToBackendFormat(values.paid_at || undefined),
            year: Number(values.year),
            quarter: Number(values.quarter),
        };

        return createPaymentMutation.mutate(payload, { onSuccess: handleSuccess, onError: handleError });
    };

    const formik = useFormik({
        onSubmit,
        initialValues,
        validationSchema,
        enableReinitialize: true,
    });

    const { isSubmitting, setFieldValue } = formik;

    /*
     * See UpdatePaymentModal: onSubmit calls `mutate` fire-and-forget, so Formik's `isSubmitting`
     * clears long before the POST resolves and Record stayed clickable throughout — every extra
     * click recorded another payment. Kept alongside this to cover the validation pass.
     */
    const isWriting = createPaymentMutation.isPending;
    const { ...values } = {
        ...formik.values,
        paid_at: convertDateToBackendFormat(formik.values.paid_at || undefined),
    };

    /*
     * Uppy posts the payment itself, so the create mutation's `onSuccess` cache work never runs.
     *
     * Not awaited: the payment is already recorded, and holding the dialog open through a refetch
     * would read as the save still being in flight.
     */
    const handleReceiptSuccess = () => {
        setIsUploadingReceipt(false);
        refreshPaymentCaches(values.payment_type as PaymentType);
        handleSuccess();
    };

    /*
     * The modal stays mounted between opens, and Uppy reports nothing when a member closes mid-
     * upload — without this, that one dismissal leaves Record disabled for every later open.
     */
    useEffect(() => {
        if (isOpen) {
            setIsUploadingReceipt(false);
        }
    }, [isOpen]);

    // Handle tax type changes
    useEffect(() => {
        const taxType = formik.values.tax_type;

        if (taxType === QTEType.FEDERAL) {
            setFieldValue('payee', 'US');
            setFieldValue('payment_type', PaymentTypes.QUARTERLY);
            return;
        }
        // Switching down from Federal otherwise strands the 'US' payee on a state or business
        // payment, with the State select showing a value it does not offer.
        if (formik.values.payee === 'US') {
            setFieldValue('payee', '');
        }
        if (taxType === QTEType.STATE) {
            setFieldValue('payment_type', PaymentTypes.QUARTERLY);
        } else if (taxType === QTEType.BUSINESS) {
            setFieldValue('payment_type', PaymentTypes.BUSINESS);
        }
    }, [formik.values.tax_type, formik.values.payee, setFieldValue]);

    return (
        <Dialog open={isOpen} onClose={onClose} fullWidth>
            <DialogTitle
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    pb: 2,
                }}>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    Record payment
                </Typography>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: 3 }}>
                <FormikProvider value={formik}>
                    <Form role="form">
                        <Stack spacing={3}>
                            <Select
                                name="tax_type"
                                label="Tax type"
                                options={taxTypeOptions}
                                placeholder="Select tax type"
                                sx={{ width: '100%' }}
                                variant="outlined"
                            />

                            {(formik.values.tax_type === QTEType.STATE ||
                                formik.values.tax_type === QTEType.BUSINESS) && (
                                <Select
                                    name="payee"
                                    label="State"
                                    options={stateOptions}
                                    placeholder="Select state"
                                    sx={{ width: '100%' }}
                                    variant="outlined"
                                />
                            )}

                            <Field name="amount">
                                {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                                {({ meta }: any) => (
                                    <AmountField
                                        name="amount"
                                        label="Amount"
                                        placeholder="$"
                                        inputComponentProps={{
                                            prefix: '$',
                                            thousandSeparator: true,
                                            decimalScale: 0,
                                            allowNegative: false,
                                        }}
                                        sx={{ flex: 1 }}
                                        variant="outlined"
                                        error={meta.touched && meta.error}
                                    />
                                )}
                            </Field>

                            <Field name="paid_at">
                                {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                                {({ meta }: any) => (
                                    <DateField
                                        name="paid_at"
                                        label="Pay date"
                                        inputFormat="MM/DD/YYYY"
                                        sx={{ flex: 1 }}
                                        TextFieldProps={{
                                            error: meta.touched && !!meta.error,
                                            helperText: meta.touched && meta.error,
                                        }}
                                        disableFuture
                                    />
                                )}
                            </Field>

                            <Stack direction="row" spacing={2}>
                                <Select
                                    name="quarter"
                                    label="Payment quarter"
                                    options={taxQuarterOptions}
                                    sx={{ flex: 1 }}
                                    variant="outlined"
                                />

                                <Select
                                    name="year"
                                    label="Tax year"
                                    options={yearOptions}
                                    placeholder="Select year"
                                    sx={{ flex: 1 }}
                                    variant="outlined"
                                />
                            </Stack>

                            <ReceiptUploader
                                onError={handleError}
                                onSuccess={handleReceiptSuccess}
                                onReplaceFile={() => setFieldValue('replace_file', true)}
                                payment={undefined}
                                trigger={trigger}
                                values={values}
                                roleScope={RoleScope.MEMBER}
                            />
                        </Stack>
                    </Form>
                </FormikProvider>
            </DialogContent>

            <DialogActions sx={{ p: 3, borderTop: `1px solid ${theme.palette.divider}` }}>
                <Button onClick={onClose} variant="outlined">
                    Cancel
                </Button>
                <Button
                    disabled={isSubmitting || isWriting || isUploadingReceipt}
                    onClick={() => {
                        formik.setTouched({
                            amount: true,
                            paid_at: true,
                            tax_type: true,
                            payee: true,
                            year: true,
                            quarter: true,
                        });
                        formik.handleSubmit();
                    }}
                    variant="contained"
                    sx={{ minWidth: 100 }}>
                    Record
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default RecordPaymentModal;
